import moment from "moment";
import { ContentStack, TDocumentDefinitions } from "pdfmake/interfaces";

import { checkPageBreakBefore } from "react-lib/apps/HISV3/common/CommonInterface";
import { getCompanyFont } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import {
  HeaderPatientDetailWide,
  HeaderPrintListProps,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

import { formatDatetime } from "react-lib/utils/dateUtils";

// Types
type ExaminationType = {
  assessment?: string;
  goal?: string;
  objective?: string;
  other_examination?: string;
  subjective?: string;
};

export type FormPhysicalTherapyCertificateProps = {
  detail: HeaderPrintListProps["detail"];
  divisionName: string;
  examination: ExaminationType;
  finish: string;
  license: string;
  medications: {
    label?: string;
    name?: string;
    quantity: number;
    unit_name?: string | null;
  }[];
  order_perform_by_fullname: string;
  post_treatment_evaluation: string;
  principleProcedure: string;
  printed_by: string;
  recommendation_education: string;
  result: string;
  secondaryProcedure: string[];
  start: string;
  treatmentOrders: string[];
};

interface ContentStackWithId extends Omit<ContentStack, "stack"> {
  id?: string;
  stack: ContentStack["stack"];
}

interface ITDocumentDefinitions extends TDocumentDefinitions {
  content: (ContentStackWithId | TDocumentDefinitions["content"])[];
}

const FONT_SIZE = 14;

const FORM_EMR_PT_CERTIFICATE = "FormPhysicalTherapyCertificate";

const fillDotValue = (data: {
  content?: TDocumentDefinitions["content"][];
  length: number;
  value: string;
  y?: number;
}): ContentStack & { width: string } => {
  const y = data.y ?? 0;

  return {
    stack: [
      {
        relativePosition: { x: 10, y: data.y ?? 0 },
        text: data.value,
      },
      {
        font: "PoppinsLM",
        fontSize: FONT_SIZE - 1,
        margin: [5, 3.5 + y, 5, 0],
        text: Array.from({ length: Math.floor(data.length) })
          .fill(".")
          .join(""),
      },
      ...(data.content ?? []),
    ],
    width: "*",
  };
};

const FormPhysicalTherapyCertificate = async (
  props: FormPhysicalTherapyCertificateProps
): Promise<ITDocumentDefinitions> => {
  const currentDate = moment();

  const headerForm = await HeaderPatientDetailWide({
    detail: {
      ...props.detail,
      divisionName: "Physical Therapy Certificate",
    },
    font: getCompanyFont(),
    form: FORM_EMR_PT_CERTIFICATE,
    formName: "",
    headerMargins: [7, 8, 8, 15],
    logoHeight: 38,
    pageMargins: [12.5, 85, 12.5, 80],
  });
  const { font, fonts, fontSizes, images, lineHeights, styles } = headerForm;

  console.log("FormPhysicalTherapyCertificate", props);

  return {
    defaultStyle: {
      font,
      fontSize: fontSizes[FONT_SIZE],
      lineHeight: lineHeights[1],
    },
    pageSize: "A4",
    ...headerForm,
    images: { ...images },
    styles: {
      ...styles,
      fieldKey: { bold: true, font: fonts.POPPINS_LM, fontSize: fontSizes[13.25] },
      miniField: { fontSize: fontSizes[12] },
      sectionHeader: {
        bold: true,
        decoration: "underline",
        font: fonts.POPPINS,
        fontSize: fontSizes[14],
        marginBottom: 2.5,
        marginTop: 5,
      },
    },
    content: [
      {
        margin: [0, 0, 15, 0],
        stack: [
          {
            columns: [
              {
                columns: [
                  { style: "fieldKey", text: "Patient's Name ", width: "auto" },
                  fillDotValue({
                    length: 132,
                    value: props.detail.patientNameEN || props.detail.patientNameTH || "",
                    y: -1,
                  }),
                ],
                width: 400,
              },
              {
                columns: [
                  { noWrap: true, style: "fieldKey", text: "HN ", width: "auto" },
                  fillDotValue({ length: 60, value: props.detail.hn, y: -1 }),
                ],
                width: "auto",
              },
            ],
          },
          {
            columns: [
              {
                text: "has received physical therapy treatment from ",
                width: "auto",
              },
              fillDotValue({ length: 130, value: props.divisionName }),
            ],
          },
          {
            columns: [
              {
                columns: [
                  {
                    text: "Performed start : ",
                    width: "auto",
                  },
                  fillDotValue({ length: 43, value: formatDatetime(props.start, true) }),
                ],
                width: "auto",
              },
              {
                columns: [
                  {
                    noWrap: true,
                    text: "Performed end : ",
                    width: "auto",
                  },
                  fillDotValue({ length: 45, value: formatDatetime(props.finish, true) }),
                ],
                width: "auto",
              },
            ],
          },
        ],
      },

      // Treatment Order Section
      {
        id: "treatment-order",
        stack: [
          { style: "sectionHeader", text: "Treatment order :" },
          {
            marginLeft: 10,
            ul: props.treatmentOrders,
          },
          { text: "Procedure code" },
          { text: "Principal procedure :" },
          {
            text: [{ text: props.principleProcedure }],
          },
          { text: "Secondary procedure :" },
          props.secondaryProcedure.length === 0
            ? { text: "-" }
            : { stack: props.secondaryProcedure.map((text) => ({ text })) },
        ],
      },

      // Examination Section
      {
        id: "examination",
        stack: [
          { style: "sectionHeader", text: "Examination" },

          { style: "fieldKey", text: "Subjective :" },
          { text: props.examination.subjective || "" },

          { style: "fieldKey", text: "Objective :" },
          { text: props.examination.objective || "" },

          { style: "fieldKey", text: "Assessment :" },
          { text: props.examination.assessment || "" },

          { style: "fieldKey", text: "Goal :" },
          { text: props.examination.goal || "" },

          { style: "fieldKey", text: "Other Examination :" },
          { text: props.examination.other_examination || "" },
        ],
      },

      // Treatment Details Section
      {
        id: "treatment-details",
        stack: [{ style: "sectionHeader", text: "Treatment Details" }, { text: props.result }],
      },

      // Post-Treatment Evaluation Section
      {
        id: "post-treatment",
        stack: [
          { style: "sectionHeader", text: "Post-Treatment Evaluation" },
          { text: props.post_treatment_evaluation },
        ],
      },

      // Recommendation & Patient Education Section
      {
        id: "recommendation",
        stack: [
          { style: "sectionHeader", text: "Recommendation & Patient Education" },
          { text: props.recommendation_education },
        ],
      },
      {
        id: "medication",
        stack: [
          { style: "sectionHeader", text: "Medication" },
          ...(props.medications.length === 0
            ? [{ text: "No medications found" }]
            : props.medications.map((item) => ({
                text: `${item.name || ""} ${item.label} จำนวน ${item.quantity} ${
                  item.unit_name || ""
                }`,
              }))),
        ],
      },
    ],
    footer: (currentPage: number, pageCount: number) => {
      const isLastPage = currentPage === pageCount;

      const footerContent: ContentStack = {
        // marginTop: 30,
        stack: [
          ...(isLastPage
            ? ([
                {
                  columns: [
                    { text: "", width: "55%" },
                    {
                      alignment: "center",
                      stack: [
                        {
                          alignment: "center",
                          columns: [
                            {
                              text: "Performed by ",
                              width: "auto",
                            },
                            {
                              ...fillDotValue({
                                length: 70,
                                value: "",
                                content: [{ text: `(${props.order_perform_by_fullname})` }],
                              }),
                              width: "auto",
                            },
                          ],
                          marginLeft: 0,
                          lineHeight: 0.8,
                        },
                        {
                          alignment: "left",
                          text: [
                            { text: "Physical Therapist  " },
                            { text: `License number  ${props.license}` },
                          ],
                          lineHeight: 0.8,
                        },
                      ],
                      width: "*",
                    },
                  ],
                },
              ] satisfies ContentStack["stack"])
            : ([
                {
                  stack: [
                    {
                      alignment: "center",
                      columns: [
                        {
                          stack: [
                            {
                              text: " ",
                            },
                            {
                              relativePosition: { x: 10, y: 0 },
                              text: " ",
                            },
                            {
                              margin: [10, 4.5, 5, 0],
                              text: " ",
                            },
                          ],
                          width: "*",
                        },
                      ],
                      marginLeft: 0,
                      lineHeight: 0.8,
                    },
                    {
                      text: " ",
                      lineHeight: 0.8,
                    },
                  ],
                },
              ] satisfies ContentStack["stack"])),
          {
            marginTop: 10,
            table: {
              widths: ["100%"],
              body: [[{ border: [false, true, false, false], text: "" }]],
            },
            layout: {
              hLineColor: () => "#606060",
              hLineWidth: () => 1,
              vLineWidth: () => 0,
            },
          },
          {
            columns: [
              {
                text: [
                  "Printed by: ",
                  { italics: true, text: props.printed_by },
                  "  ",
                  { text: formatDatetime(currentDate, true) },
                ],
              },
              { alignment: "right", text: `Page ${currentPage} of ${pageCount}` },
            ],
            margin: [15, 2, 15, 0],
            style: "miniField",
          },
        ],
      };

      return footerContent;
    },
    // pageBreakBefore: (currentNode, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage) => {
    //   const config = {
    //     ids: ["examination", "treatment-details", "post-treatment", "recommendation", "medication"],
    //   };

    //   const checkBreak = checkPageBreakBefore(config);

    //   return checkBreak(currentNode, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage);
    // },
  };
};

export default FormPhysicalTherapyCertificate;
